import { useQueryClient } from "@tanstack/react-query";
import { Empty, Loader } from "@zfp-akademie-a-s/zfp-design";
import { ErrorIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { ProposedSolutionDeleteDialog } from "Components/Analyses/GoalsFulfillment/ProposedSolutionDeleteDialog";
import { invalidateProposalQueries } from "Components/Analyses/GoalsFulfillment/invalidateProposalQueries";
import { useDeleteContractProposal, useGetContractProposal } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { ContractProposalBaseInvestmentToolType, ContractProposalBaseSolutionHorizonType } from "Infrastructure/ApiZfp/Schema";
import type { ContractProposalDetailInvestment } from "Infrastructure/ApiZfp/Schema/contractProposalDetailInvestment";
import type { NsurePlanOverview } from "Infrastructure/ApiZfp/Schema/nsurePlanOverview";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useMemo, useState } from "react";
import { toast } from "sonner";
import { InvestmentsNsureDetails } from "./InvestmentsNsureDetails";
import { InvestmentsNsureSelectStep } from "./InvestmentsNsureSelectStep";
import { type NsureSelection, NsureSelectionProvider } from "./InvestmentsNsureSelectionContext";
import { buildInitialNsureSelection } from "./helpers";

type Props = {
  readonly edit?: boolean;
  readonly proposedSolutionId?: string;
  readonly onFormTypeChange: (type: ContractProposalBaseInvestmentToolType) => void;
  readonly onClose: () => void;
  readonly nsurePlan: NsurePlanOverview;
  readonly horizonType: ContractProposalBaseSolutionHorizonType;
};

export const InvestmentsNsureWrapper = ({ edit, proposedSolutionId, nsurePlan, onFormTypeChange, onClose, horizonType }: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const queryClient = useQueryClient();
  // Editing an existing proposal opens the form right away — the selection step would only repeat the
  // instrument detail that the form step already shows in its right column.
  const [currentStep, setCurrentStep] = useState<1 | 2>(edit ? 2 : 1);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { mutate: deleteMutation, isPending: isDeletePending } = useDeleteContractProposal({
    mutation: {
      onSuccess: () => invalidateProposalQueries(queryClient, analysisId, versionId),
      onError: () => toast.error(t(Resources.AnalysisDetail.Toast.ActionError)),
    },
  });

  const deleteSolution = () => {
    if (!edit || !proposedSolutionId) return;
    deleteMutation(
      { analysisId, versionId, contractProposalGuid: proposedSolutionId },
      {
        onSuccess: () => {
          setIsDeleteDialogOpen(false);
          onClose();
        },
        onError: () => setIsDeleteDialogOpen(false),
      },
    );
  };

  const {
    data: solutionData,
    isError,
    isFetching,
  } = useGetContractProposal(analysisId, versionId, proposedSolutionId ?? "", {
    query: { enabled: !!proposedSolutionId && !!edit },
  });

  const investmentData = solutionData as ContractProposalDetailInvestment | undefined;

  const initialSelection = useMemo<NsureSelection>(() => buildInitialNsureSelection(edit, investmentData), [edit, investmentData]);

  const initialShowInstrumentSearch = !!edit && !!investmentData && investmentData.portfolio?.strategyTemplateID == null;

  if (edit && proposedSolutionId && (!solutionData || isFetching)) {
    return isError ? (
      <Empty
        title={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Title)}
        text={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Text)}
        iconSize={40}
        icon={<ErrorIcon sx={{ color: "grey.400" }} />}
      />
    ) : (
      <Loader />
    );
  }

  const openDeleteDialog = edit ? () => setIsDeleteDialogOpen(true) : undefined;

  return (
    <NsureSelectionProvider
      nsurePlan={nsurePlan}
      initialSelection={initialSelection}
      initialShowInstrumentSearch={initialShowInstrumentSearch}
    >
      {currentStep === 2 ? (
        <InvestmentsNsureDetails
          onBack={() => setCurrentStep(1)}
          onClose={onClose}
          onDelete={openDeleteDialog}
          horizonType={horizonType}
          edit={edit}
          proposedSolutionId={proposedSolutionId}
          initialData={investmentData}
        />
      ) : (
        <InvestmentsNsureSelectStep
          onClose={onClose}
          onContinue={() => setCurrentStep(2)}
          onDelete={openDeleteDialog}
          isEditMode={edit ?? false}
          onFormTypeChange={onFormTypeChange}
        />
      )}

      <ProposedSolutionDeleteDialog
        isOpen={isDeleteDialogOpen}
        isPending={isDeletePending}
        onClose={() => !isDeletePending && setIsDeleteDialogOpen(false)}
        onConfirm={deleteSolution}
      />
    </NsureSelectionProvider>
  );
};
